import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller } from "react-hook-form";
import {
  CarrierCodePickable,
  CarrierCodePickableType,
  useCarriersVariables,
} from "../use-carriers-variables";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { Switch } from "@headlessui/react";
import { Button } from "@babackapp/ui/components/button";
import { Input } from "@babackapp/ui/components/input";
import { useCarrierTransportForm } from "../hooks/use-carrier-transport-form";
import useTranslation from "@babackapp/translations/use-translation";
import i18n from "i18next";
import {
  CreateCarrierDto,
  UpdateCarrierDto,
  UpsCarrierPreferenceDto,
} from "@babackapp/private-api-client/types";
import { useCarriersContext } from "../../use-carriers-context";
import { FedexCspRegistrationForm } from "./fedex-csp-registration-form";

type FedexMode = "legacy" | "csp";

export function CarrierTransportForm({
  defaultValues,
  onSubmit,
  onSubmitUps,
  onFedexRegistrationComplete,
}: {
  defaultValues?: UpdateCarrierDto;
  onSubmit: (data: CreateCarrierDto | UpdateCarrierDto) => void;
  onSubmitUps: (data: { accountNumber: string; carrierType: string }) => void;
  onFedexRegistrationComplete?: () => void;
}) {
  const t = useTranslation();
  const { shop } = useCarriersContext();
  const [infoUrl, setInfoUrl] = useState("");
  const [fedexMode] = useState<FedexMode>("csp");

  const { listCarriers, infoImageUrlByLocale } = useCarriersVariables();
  const {
    onChangeCarrierPicked,
    carrierCodeSelected,
    carrierFormFields,
    errors,
    isValid,
    handleSubmit,
    control,
    register,
  } = useCarrierTransportForm({
    onSubmit: (data) => {
      if (["ups-saver", "ups-standard", "ups-ground"].includes(data.code)) {
        onSubmitUps({
          accountNumber: (data.credentials as UpsCarrierPreferenceDto)
            .accountNumber as string,
          carrierType: carrierCodeSelected as CarrierCodePickable,
        });
      } else {
        onSubmit(data);
      }
    },
    defaultValues: {
      code: defaultValues?.code as CarrierCodePickableType,
      credentials: defaultValues?.credentials as { [key: string]: string },
    },
  });

  const getLocaleLanguage = useMemo(() => {
    return i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    setInfoUrl(infoImageUrlByLocale[getLocaleLanguage]);
  }, [i18n.language]);

  const onClickInformations = useCallback(() => {
    window.open("https://help.baback.co/fr/articles/8068669-ups", "_blank");
  }, []);

  const isFedexSelected = carrierCodeSelected === "fedex";
  const isFedexCsp = isFedexSelected && fedexMode === "csp";

  return (
    <>
      <select
        onChange={onChangeCarrierPicked}
        value={carrierCodeSelected as string}
        id="carrier"
        name="carrier"
        className="flex flex-1 w-full border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm p-2 mt-2 mb-2"
      >
        <option value="">{t("carrier.selectlabel")}</option>
        {listCarriers.map((carrier, key) => (
          <option key={key} value={carrier.value}>
            {carrier.label}
          </option>
        ))}
      </select>

      {isFedexSelected ? (
        <FedexCspRegistrationForm
          shopId={shop.id}
          shop={shop}
          defaultAccountNumber={
            defaultValues?.credentials?.accountNumber as string | undefined
          }
          onRegistrationComplete={onFedexRegistrationComplete ?? (() => {})}
        />
      ) : (
        <>
          {Object.entries(carrierFormFields || {}).map(
            (
              [mappedCarrierField, fieldProps]: [
                string,
                {
                  label: string;
                  type?: string;
                  options?: { value: string; label: string }[];
                  placeholder?: string | boolean;
                  hint?: string;
                },
              ],
              key,
            ) => (
              <div className="mt-2" key={key}>
                <div className="flex items-center space-x-2">
                  <label
                    htmlFor={mappedCarrierField}
                    className="block text-sm font-medium text-gray-700"
                  >
                    {fieldProps.label}
                  </label>
                  {["ups-saver", "ups-standard", "ups-ground"].includes(
                    carrierCodeSelected as CarrierCodePickable,
                  ) &&
                    mappedCarrierField === "accountNumber" && (
                      <InformationCircleIcon
                        className="h-5 w-5 text-gray-700 cursor-pointer"
                        aria-hidden="true"
                        onClick={onClickInformations}
                      />
                    )}
                </div>
                <div className="mt-1">
                  {mappedCarrierField !== "SLS" &&
                    mappedCarrierField !== "isDev" && (
                      <>
                        {fieldProps.type === "select" ? (
                          <select
                            {...register(mappedCarrierField)}
                            id={mappedCarrierField}
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 mt-1 block w-full sm:text-sm border border-gray-300 rounded-md p-2"
                          >
                            <option value="">{t("carrier.selectlabel")}</option>
                            {fieldProps.options?.map(
                              (
                                option: { value: string; label: string },
                                idx: number,
                              ) => (
                                <option key={idx} value={option.value}>
                                  {option.label}
                                </option>
                              ),
                            )}
                          </select>
                        ) : (
                          <Input
                            {...register(mappedCarrierField)}
                            type={fieldProps.type || "text"}
                            id={mappedCarrierField}
                            // Carrier credentials are not the browser's to
                            // remember. Chrome autofilled a stale value into
                            // the DHL API key field between two attempts,
                            // which read as the merchant re-entering a wrong
                            // key (BBS-330).
                            autoComplete="off"
                            data-1p-ignore
                            data-lpignore="true"
                            className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 mt-1 block w-full sm:text-sm border border-gray-300 rounded-md"
                            placeholder={
                              typeof fieldProps.placeholder === "string"
                                ? fieldProps.placeholder
                                : ""
                            }
                          />
                        )}
                        {fieldProps.hint && (
                          <p className="mt-1 text-xs text-gray-500">
                            {fieldProps.hint}
                          </p>
                        )}
                        {errors.code && (
                          <span className="text-red-500 text-sm">
                            {errors.code.message}
                          </span>
                        )}
                      </>
                    )}
                  {carrierCodeSelected === "mondial-relay" &&
                    mappedCarrierField === "livRel" && (
                      <>
                        <a
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm ml-1 mt-1 font-regular text-gray-900 hover:text-purple-600"
                          href="https://www.mondialrelay.fr/trouver-le-point-relais-le-plus-proche-de-chez-moi/"
                        >
                          {t("carrier.findRelayPoint")}
                        </a>
                        <div className="rounded-md mt-2 h-auto w-auto overflow-hidden bg-gray-100">
                          <img src={infoUrl} alt="Information" />
                        </div>
                      </>
                    )}
                  {carrierCodeSelected === "colissimo" &&
                    mappedCarrierField === "SLS" && (
                      <Controller
                        name={mappedCarrierField}
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <Switch
                            checked={field.value as boolean}
                            onChange={field.onChange}
                            className={`${
                              field.value ? "bg-purple-600" : "bg-gray-200"
                            } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full  transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500`}
                          >
                            <span className="sr-only">Use setting</span>
                            <span
                              className={`${
                                field.value ? "translate-x-5" : "translate-x-0"
                              } pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                            >
                              {/* Icons can be added here if needed */}
                            </span>
                          </Switch>
                        )}
                      />
                    )}
                  {carrierCodeSelected === "fedex" &&
                    mappedCarrierField === "isDev" && (
                      <div className="flex flex-col gap-1">
                        <Controller
                          name={mappedCarrierField}
                          control={control}
                          defaultValue={false}
                          render={({ field }) => (
                            <Switch
                              checked={field.value as boolean}
                              onChange={field.onChange}
                              className={`${
                                field.value ? "bg-purple-600" : "bg-gray-200"
                              } relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full  transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500`}
                            >
                              <span className="sr-only">Use setting</span>
                              <span
                                className={`${
                                  field.value
                                    ? "translate-x-5"
                                    : "translate-x-0"
                                } pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200`}
                              >
                                {/* Icons can be added here if needed */}
                              </span>
                            </Switch>
                          )}
                        />
                      </div>
                    )}
                </div>
              </div>
            ),
          )}

          <div className="flex flex-col space-y-1">
            <Button
              variant="default"
              size="lg"
              disabled={!isValid}
              onClick={handleSubmit}
            >
              {t("misc.validate")}
            </Button>
          </div>
        </>
      )}
    </>
  );
}
